import mensagens from './mensagens';
import {
	DadosGraficoVariaveis,
	DadosSerieVariavelMeteorologica,
	SerieVariavelMeteorologica,
} from './serie-variavel-meteorologica';
import { getOptions } from 'highcharts';

const seriaBaseDirecaoVentoID = 'serie-base-direcao-vento';

export interface DadosVento extends DadosGraficoVariaveis {
	velocidadeVento: Array<number | null>;
	direcaoVento?: Array<number | null>;
}

export class SerieVento extends SerieVariavelMeteorologica {
	private corVelocidadeVento = getOptions().colors?.at(0);
	private corDirecaoVento = getOptions().colors?.at(1);

	obterDadosSerie(
		dadosGrafico: DadosVento,
		indexEixo: number
	): DadosSerieVariavelMeteorologica {
		const serie = [
			this.obterSerieVelocidadeVento(
				dadosGrafico,
				indexEixo,
				dadosGrafico.periodo
			),
		];
		if (dadosGrafico.direcaoVento?.some(direcao => this.isNumeroValido(direcao))) {
			serie.push(
				this.obterSerieBaseDirecaoVento(
					dadosGrafico,
					indexEixo,
					dadosGrafico.periodo
				),
				this.obterSerieDirecaoVento(
					dadosGrafico,
					indexEixo,
					dadosGrafico.periodo
				)
			);
		}
		return {
			serie,
			eixo: {
				title: {
					text: mensagens['variavel.vento.titulo-meteograma'],
				},
				offset: 0,
				lineWidth: 2,
				opposite: false,
				labels: { align: 'left', x: 3 },
			},
		};
	}

	private obterSerieVelocidadeVento(
		dadosGrafico: DadosVento,
		indexEixo: number,
		periodo?: string
	): Object {
		if (this.corVelocidadeVento) {
			return {
				type: 'line',
				yAxis: indexEixo,
				data: this.prepararSerieVelocidadeVento(dadosGrafico, periodo),
				color: this.corVelocidadeVento,
				name: mensagens['variavel.velocidade'],
				tooltip: {
					valueDecimals: 2,
					valueSuffix: `${mensagens['variavel.velocidade.unidade']}`,
				},
			};
		}
		return {};
	}

	private obterSerieBaseDirecaoVento(
		dadosGrafico: DadosVento,
		indexEixo: number,
		periodo?: string
	): Object {
		return {
			id: seriaBaseDirecaoVentoID,
			type: 'line',
			yAxis: indexEixo,
			data: this.prepararSerieBaseDirecaoVento(dadosGrafico, periodo),
			color: 'transparent',
			enableMouseTracking: false,
			includeInDataExport: false,
			showInLegend: false,
			marker: {
				enabled: false,
			},
			states: {
				inactive: {
					opacity: 0,
				},
			},
		};
	}

	private obterSerieDirecaoVento(
		dadosGrafico: DadosVento,
		indexEixo: number,
		periodo?: string
	): Object {
		const dadosFormatados = this.prepararSerieVento(dadosGrafico, periodo);

		return {
			type: 'windbarb',
			yAxis: indexEixo,
			onSeries: seriaBaseDirecaoVentoID,
			keys: ['x', 'value', 'direction'],
			data: dadosFormatados,
			name: mensagens['variavel.direcao'],
			color: this.corDirecaoVento,
			showInLegend: false,
			vectorLength: 18,
			yOffset: -10,
			turboThreshold: dadosFormatados.length,
			tooltip: {
				valueDecimals: 2,
				valueSuffix: ` ${mensagens['variavel.direcao.unidade']}`,
			},
		};
	}

	private prepararSerieVelocidadeVento(
		{ datas, velocidadeVento }: DadosVento,
		periodo?: string
	) {
		if (!datas?.length || !velocidadeVento?.length) {
			console.warn('Dados incompletos para montar a série de velocidade:', {
				datas,
				velocidadeVento,
			});
			return [];
		}

		const serie = [];
		for (let i = 0; i < datas.length; i++) {
			const velocidade = velocidadeVento[i];
			const timestamp = this.obterTimestamp(datas[i], periodo);
			if (!Number.isFinite(timestamp) || !this.isNumeroValido(velocidade)) {
				continue;
			}
			serie.push([timestamp, velocidade]);
		}
		return serie;
	}

	private prepararSerieBaseDirecaoVento(
		{ datas, velocidadeVento }: DadosVento,
		periodo?: string
	) {
		if (!datas?.length || !velocidadeVento?.length) {
			return [];
		}

		const velocidadesValidas = velocidadeVento.filter(
			(velocidade): velocidade is number => this.isNumeroValido(velocidade)
		);
		if (velocidadesValidas.length === 0) {
			return [];
		}

		const baseDirecaoVento = Math.min(...velocidadesValidas);
		const serie = [];
		for (let i = 0; i < datas.length; i++) {
			const timestamp = this.obterTimestamp(datas[i], periodo);
			if (!Number.isFinite(timestamp)) {
				continue;
			}
			serie.push([timestamp, baseDirecaoVento]);
		}
		return serie;
	}

	private prepararSerieVento(
		{ datas, velocidadeVento, direcaoVento }: DadosVento,
		periodo?: string
	) {
		if (!datas?.length || !velocidadeVento?.length || !direcaoVento?.length) {
			console.warn('Dados incompletos para montar o gráfico de vento:', {
				datas,
				velocidadeVento,
				direcaoVento,
			});
			return [];
		}

		const serie = [];
		for (let i = 0; i < datas.length; i++) {
			const velocidade = velocidadeVento[i];
			const direcao = direcaoVento[i];
			const timestamp = this.obterTimestamp(datas[i], periodo);
			if (
				!Number.isFinite(timestamp) ||
				!this.isNumeroValido(velocidade) ||
				!this.isNumeroValido(direcao)
			) {
				continue;
			}
			serie.push([timestamp, velocidade, direcao]);
		}
		return serie;
	}

	private obterTimestamp(data: Date | string | number, _periodo?: string): number {
		const dateObj = data instanceof Date ? data : new Date(data);
		return dateObj.getTime();
	}

	private isNumeroValido(valor: number | null | undefined): valor is number {
		return typeof valor === 'number' && Number.isFinite(valor);
	}
}
